import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart, useRemoveCartItem, useUpdateCartItem, useApplyCoupon, useRemoveCoupon, useUpdateCartBundle, useRemoveCartBundle } from '../hooks/useCart';
import { useCurrency } from '../hooks/useCurrency';
import { Container } from '../components/ui/Container';
import { EditorialImage } from '../components/ui/EditorialImage';
import type { CartItem, CartBundle } from '../types';

const API_URL = import.meta.env.VITE_API_URL as string;

function bundleImgSrc(path: string | null): string | null {
  if (!path) return null;
  return path.startsWith('http') ? path : `${API_URL}${path}`;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function variantLabel(item: CartItem): string | null {
  const v = item.variant;
  const p = item.product;
  if (!v || !p) return null;
  const parts: string[] = [];
  if (v.size_id && p.sizes) {
    const s = p.sizes.find((x) => x.id === v.size_id);
    if (s) parts.push(s.name);
  }
  if (v.material_id && p.materials) {
    const m = p.materials.find((x) => x.id === v.material_id);
    if (m) parts.push(m.name);
  }
  if (v.frame_id && p.frames) {
    const f = p.frames.find((x) => x.id === v.frame_id);
    if (f) parts.push(f.name);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="flex gap-5 rounded-2xl border border-stone-light bg-paper p-5 animate-pulse">
      <div className="h-28 w-28 shrink-0 rounded-xl bg-stone-light/50" />
      <div className="flex-1 space-y-3 py-1">
        <div className="h-4 w-2/3 rounded bg-stone-light/60" />
        <div className="h-3 w-1/3 rounded bg-stone-light/40" />
        <div className="mt-4 h-8 w-24 rounded-full bg-stone-light/40" />
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="h-3 w-16 rounded bg-stone-light/40" />
        <div className="h-5 w-20 rounded bg-stone-light/60" />
      </div>
    </div>
  );
}

// ─── Product Card ─────────────────────────────────────────────────────────────

function CartItemCard({ item }: { item: CartItem }) {
  const { format } = useCurrency();
  const updateItem = useUpdateCartItem();
  const removeItem = useRemoveCartItem();
  const [removing, setRemoving] = useState(false);

  const _rawPath = item.product?.images?.[0]?.path;
  const imgSrc = _rawPath ? (_rawPath.startsWith('http') ? _rawPath : `${import.meta.env.VITE_API_URL}${_rawPath}`) : null;
  const name = item.product?.name ?? `Product #${item.product_id}`;
  const spec = variantLabel(item);
  const lineTotal = Number(item.unit_price) * item.quantity;

  const handleRemove = () => {
    setRemoving(true);
    removeItem.mutate(item.id);
  };

  return (
    <div
      className={`group flex gap-4 sm:gap-6 rounded-2xl border border-stone-light bg-paper p-4 sm:p-5 transition-all duration-300 ${removing ? 'opacity-0 scale-95' : 'opacity-100'}`}
    >
      {/* Image */}
      <div className="relative h-24 w-24 sm:h-32 sm:w-32 shrink-0 overflow-hidden rounded-xl">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <EditorialImage
            seed={`cart-item-${item.id}`}
            alt={name}
            duotone={false}
            size="thumb"
            aspect="aspect-square"
            className="h-full w-full"
          />
        )}
      </div>

      {/* Details */}
      <div className="flex flex-1 flex-col justify-between min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-serif text-base font-medium text-ink leading-snug line-clamp-2">{name}</p>
            {spec && (
              <p className="mt-1 text-xs text-stone tracking-wide">{spec}</p>
            )}
            {(item.size_label || item.frame_name || item.material_name) && (
              <div className="mt-1 flex flex-wrap gap-1">
                {item.size_label && (
                  <span className="rounded-full bg-parchment px-2 py-0.5 text-[10px] text-stone">{item.size_label}</span>
                )}
                {item.frame_name && (
                  <span className="rounded-full bg-parchment px-2 py-0.5 text-[10px] text-stone">{item.frame_name}</span>
                )}
                {item.material_name && (
                  <span className="rounded-full bg-parchment px-2 py-0.5 text-[10px] text-stone">{item.material_name}</span>
                )}
              </div>
            )}
            <p className="mt-1.5 text-xs text-stone">{format(item.unit_price)} each</p>
          </div>
          {/* Line total (desktop) */}
          <div className="hidden sm:flex flex-col items-end shrink-0">
            <p className="text-sm font-semibold text-ink tabular-nums">{format(lineTotal)}</p>
          </div>
        </div>

        {/* Qty + Remove */}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-0.5 rounded-full border border-stone-light bg-cream">
            <button
              type="button"
              onClick={() => {
                if (item.quantity > 1) updateItem.mutate({ id: item.id, quantity: item.quantity - 1 });
              }}
              disabled={item.quantity <= 1 || updateItem.isPending}
              className="flex h-8 w-8 items-center justify-center rounded-full text-stone transition-colors hover:bg-stone-light/40 hover:text-ink disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Decrease quantity"
            >
              <svg width="12" height="2" viewBox="0 0 12 2" fill="currentColor"><rect width="12" height="2" rx="1"/></svg>
            </button>
            <span className="w-7 text-center text-sm font-medium text-ink tabular-nums select-none">
              {item.quantity}
            </span>
            <button
              type="button"
              onClick={() => updateItem.mutate({ id: item.id, quantity: item.quantity + 1 })}
              disabled={updateItem.isPending}
              className="flex h-8 w-8 items-center justify-center rounded-full text-stone transition-colors hover:bg-stone-light/40 hover:text-ink disabled:opacity-40"
              aria-label="Increase quantity"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                <rect x="5" width="2" height="12" rx="1"/>
                <rect y="5" width="12" height="2" rx="1"/>
              </svg>
            </button>
          </div>

          <div className="flex items-center gap-4">
            {/* Line total (mobile) */}
            <p className="text-sm font-semibold text-ink tabular-nums sm:hidden">{format(lineTotal)}</p>
            <button
              type="button"
              onClick={handleRemove}
              disabled={removeItem.isPending}
              className="text-xs text-stone/70 transition-colors hover:text-clay disabled:opacity-40"
            >
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Bundle Card ─────────────────────────────────────────────────────────────

function CartBundleCard({ bundle }: { bundle: CartBundle }) {
  const { format } = useCurrency();
  const updateBundle = useUpdateCartBundle();
  const removeBundle = useRemoveCartBundle();
  const [removing, setRemoving] = useState(false);

  const imgSrc = bundleImgSrc(bundle.bundle_image);
  const lineTotal = Number(bundle.unit_price) * bundle.quantity;
  const originalLineTotal = Number(bundle.original_price) * bundle.quantity;
  const discountLineTotal = Number(bundle.discount_amount) * bundle.quantity;

  const handleRemove = () => {
    setRemoving(true);
    removeBundle.mutate(bundle.id);
  };

  return (
    <div
      className={`group rounded-2xl border border-stone-light bg-paper p-4 sm:p-5 transition-all duration-300 ${removing ? 'opacity-0 scale-95' : 'opacity-100'}`}
    >
      {/* Bundle header */}
      <div className="flex items-start gap-4">
        {/* Image */}
        <div className="relative h-24 w-24 sm:h-28 sm:w-28 shrink-0 overflow-hidden rounded-xl">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={bundle.bundle_name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-xl bg-parchment text-2xl">📦</div>
          )}
        </div>

        {/* Title + pricing */}
        <div className="flex flex-1 flex-col justify-between min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-serif text-base font-medium text-ink leading-snug">{bundle.bundle_name}</p>
              <p className="mt-0.5 text-xs text-stone">
                {bundle.products.length} {bundle.products.length === 1 ? 'product' : 'products'} included
              </p>
            </div>
            <div className="hidden sm:flex flex-col items-end shrink-0">
              {discountLineTotal > 0 && (
                <p className="text-xs text-stone line-through tabular-nums">{format(originalLineTotal)}</p>
              )}
              <p className="text-sm font-semibold text-ink tabular-nums">{format(lineTotal)}</p>
            </div>
          </div>

          {/* Qty + Remove */}
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-0.5 rounded-full border border-stone-light bg-cream">
              <button
                type="button"
                onClick={() => {
                  if (bundle.quantity > 1) updateBundle.mutate({ id: bundle.id, quantity: bundle.quantity - 1 });
                }}
                disabled={bundle.quantity <= 1 || updateBundle.isPending}
                className="flex h-8 w-8 items-center justify-center rounded-full text-stone transition-colors hover:bg-stone-light/40 hover:text-ink disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Decrease quantity"
              >
                <svg width="12" height="2" viewBox="0 0 12 2" fill="currentColor"><rect width="12" height="2" rx="1"/></svg>
              </button>
              <span className="w-7 text-center text-sm font-medium text-ink tabular-nums select-none">
                {bundle.quantity}
              </span>
              <button
                type="button"
                onClick={() => updateBundle.mutate({ id: bundle.id, quantity: bundle.quantity + 1 })}
                disabled={updateBundle.isPending}
                className="flex h-8 w-8 items-center justify-center rounded-full text-stone transition-colors hover:bg-stone-light/40 hover:text-ink disabled:opacity-40"
                aria-label="Increase quantity"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                  <rect x="5" width="2" height="12" rx="1"/>
                  <rect y="5" width="12" height="2" rx="1"/>
                </svg>
              </button>
            </div>

            <div className="flex items-center gap-4">
              <p className="text-sm font-semibold text-ink tabular-nums sm:hidden">{format(lineTotal)}</p>
              <button
                type="button"
                onClick={handleRemove}
                disabled={removeBundle.isPending}
                className="text-xs text-stone/70 transition-colors hover:text-clay disabled:opacity-40"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Included products */}
      <div className="mt-4 space-y-2 border-t border-stone-light/60 pt-3">
        {bundle.products.map((p, i) => {
          const pImg = p.product_image ? (p.product_image.startsWith('http') ? p.product_image : `${API_URL}${p.product_image}`) : null;
          return (
            <div key={i} className="flex items-center gap-3">
              <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-parchment">
                {pImg ? (
                  <img src={pImg} alt={p.product_name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-sm text-stone/40">🖼</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium text-ink-soft">{p.product_name}</p>
                <p className="truncate text-xs text-stone">
                  {[
                    p.size_label,
                    p.frame_name,
                    p.material_name,
                  ].filter(Boolean).join(' · ') || 'Standard'}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-medium text-ink-soft tabular-nums">{format(Number(p.unit_price))}</p>
                {p.quantity > 1 && <p className="text-[10px] text-stone">× {p.quantity}</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bundle savings badge */}
      {discountLineTotal > 0 && (
        <div className="mt-3 flex items-center justify-between rounded-lg bg-moss/8 border border-moss/15 px-3 py-2">
          <span className="text-xs text-moss font-medium">
            Bundle discount ({bundle.discount_percent > 0 ? `${Math.round(bundle.discount_percent)}%` : format(Number(bundle.discount_amount))} off)
          </span>
          <span className="text-xs font-semibold text-moss tabular-nums">
            You save {format(discountLineTotal)}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Coupon Section ───────────────────────────────────────────────────────────

function CouponSection() {
  const { data: cart } = useCart();
  const applyCoupon = useApplyCoupon();
  const removeCoupon = useRemoveCoupon();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const couponCode = cart?.coupon_code ?? null;

  const handleApply = () => {
    if (!code.trim()) return;
    setError(null);
    applyCoupon.mutate({ code: code.trim() }, {
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        setError(msg ?? 'Invalid coupon code.');
      },
    });
  };

  if (couponCode) {
    return (
      <div className="flex items-center justify-between rounded-xl border border-moss/30 bg-moss/5 px-4 py-3">
        <div className="flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-moss shrink-0">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
            <line x1="7" y1="7" x2="7.01" y2="7"/>
          </svg>
          <span className="text-sm font-medium text-moss">{couponCode} applied</span>
        </div>
        <button
          type="button"
          onClick={() => removeCoupon.mutate()}
          className="text-xs text-stone underline decoration-stone-light underline-offset-2 hover:text-clay"
        >
          Remove
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-stone-light">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-ink-soft"
      >
        <div className="flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-stone">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
            <line x1="7" y1="7" x2="7.01" y2="7"/>
          </svg>
          <span>Coupon code</span>
        </div>
        <svg
          width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className={`text-stone transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div className="border-t border-stone-light px-4 pb-4 pt-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => { setCode(e.target.value); setError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleApply()}
              placeholder="Enter code"
              className="flex-1 rounded-lg border border-stone-light bg-cream px-3 py-2 text-sm text-ink placeholder:text-stone/60 focus:border-ink focus:outline-none"
            />
            <button
              type="button"
              onClick={handleApply}
              disabled={applyCoupon.isPending || !code.trim()}
              className="shrink-0 rounded-lg bg-ink px-4 py-2 text-xs font-semibold uppercase tracking-widest text-cream transition-colors hover:bg-clay disabled:opacity-40"
            >
              {applyCoupon.isPending ? '…' : 'Apply'}
            </button>
          </div>
          {error && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-clay">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Trust Strip ─────────────────────────────────────────────────────────────

function TrustStrip() {
  const badges = [
    {
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
      label: 'Secure Checkout',
    },
    {
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <rect x="1" y="3" width="22" height="18" rx="3"/><path d="M1 9h22"/>
        </svg>
      ),
      label: 'Safe Payments',
    },
    {
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <polyline points="23 7 13.5 15.5 8.5 10.5 1 17"/><polyline points="17 7 23 7 23 13"/>
        </svg>
      ),
      label: 'Free Returns',
    },
  ];
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 pt-1">
      {badges.map((b) => (
        <div key={b.label} className="flex items-center gap-1.5 text-[11px] text-stone">
          <span className="text-stone/60">{b.icon}</span>
          {b.label}
        </div>
      ))}
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyCart() {
  return (
    <div className="flex flex-col items-center py-24 text-center">
      <div className="flex h-24 w-24 items-center justify-center rounded-full border border-stone-light bg-paper">
        <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="text-stone">
          <path d="M6 6h15l-1.5 9h-12z"/><path d="M6 6L4.5 3H2"/>
          <circle cx="9.5" cy="20" r="1.2" fill="currentColor" stroke="none"/>
          <circle cx="17.5" cy="20" r="1.2" fill="currentColor" stroke="none"/>
        </svg>
      </div>
      <h2 className="mt-7 font-serif text-2xl font-light text-ink">Your cart is empty</h2>
      <p className="mt-3 max-w-xs text-sm leading-relaxed text-stone">
        Looks like you haven't added anything yet. Explore our collection and find something you love.
      </p>
      <Link
        to="/"
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3 text-[13px] font-medium uppercase tracking-widest text-cream transition-colors hover:bg-clay"
      >
        Continue shopping
      </Link>
    </div>
  );
}

// ─── Cart Page ────────────────────────────────────────────────────────────────

export function CartPage() {
  const { data: cart, isLoading } = useCart();
  const { format } = useCurrency();

  const items = cart?.items ?? [];
  const bundles = cart?.bundles ?? [];
  const subtotal = cart?.subtotal ?? items.reduce((sum, item) => sum + Number(item.unit_price) * item.quantity, 0) + bundles.reduce((sum, b) => sum + Number(b.unit_price) * b.quantity, 0);
  const bundleDiscount = bundles.reduce((sum, b) => sum + Number(b.discount_amount) * b.quantity, 0);
  // cart.subtotal from backend already uses discounted bundle unit_price,
  // so add back the bundle discount to show the original subtotal
  const displaySubtotal = subtotal + bundleDiscount;
  const saleDiscount = cart?.sale_discount ?? 0;
  const freeItems = cart?.free_items ?? [];
  const couponDiscount = cart?.coupon_discount ?? 0;
  const couponCode = cart?.coupon_code ?? null;
  const total = cart?.total ?? subtotal;
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0) + bundles.reduce((sum, b) => sum + b.quantity, 0);
  const totalSavings = saleDiscount + couponDiscount + bundleDiscount;
  const hasItems = items.length > 0 || bundles.length > 0;

  return (
    <Container className="py-12 md:py-16">
      {/* Header */}
      <div className="flex items-baseline gap-3 border-b border-stone-light pb-6">
        <h1 className="font-serif text-3xl font-light text-ink md:text-4xl">Your Cart</h1>
        {totalItems > 0 && (
          <span className="text-sm text-stone">
            {totalItems} {totalItems === 1 ? 'item' : 'items'}
          </span>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_360px]">
          <div className="space-y-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
          <div className="h-80 animate-pulse rounded-2xl bg-stone-light/30" />
        </div>
      )}

      {/* Empty */}
      {!isLoading && !hasItems && <EmptyCart />}

      {/* Cart with items */}
      {!isLoading && hasItems && (
        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px] lg:items-start">
          {/* Items list */}
          <div className="space-y-4">
            {items.map((item) => (
              <CartItemCard key={item.id} item={item} />
            ))}
            {bundles.map((bundle) => (
              <CartBundleCard key={bundle.id} bundle={bundle} />
            ))}
            <div className="pt-2">
              <Link
                to="/"
                className="flex items-center gap-2 text-sm text-stone transition-colors hover:text-ink"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
                Continue shopping
              </Link>
            </div>
          </div>

          {/* Sticky Summary */}
          <div className="lg:sticky lg:top-24">
            <div className="rounded-2xl border border-stone-light bg-paper shadow-sm">
              <div className="border-b border-stone-light px-6 py-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone">Order Summary</p>
              </div>

              <div className="px-6 py-5 space-y-3">
                <div className="flex justify-between text-sm text-ink-soft">
                  <span>Subtotal ({totalItems} {totalItems === 1 ? 'item' : 'items'})</span>
                  <span className="tabular-nums">{format(displaySubtotal)}</span>
                </div>

                {bundleDiscount > 0 && (
                  <div className="flex justify-between text-sm text-moss">
                    <span>Bundle discount</span>
                    <span className="tabular-nums">−{format(bundleDiscount)}</span>
                  </div>
                )}

                {freeItems.length > 0
                  ? freeItems.map((free, idx) => (
                      <div key={idx} className="flex justify-between text-sm text-moss">
                        <span>{free.sale_name}: {free.quantity} free</span>
                        <span className="tabular-nums">−{format(free.free_unit_price * free.quantity)}</span>
                      </div>
                    ))
                  : saleDiscount > 0 && (
                      <div className="flex justify-between text-sm text-moss">
                        <span>Sale discount</span>
                        <span className="tabular-nums">−{format(saleDiscount)}</span>
                      </div>
                    )
                }

                {couponDiscount > 0 && couponCode && (
                  <div className="flex justify-between text-sm text-moss">
                    <span>Coupon ({couponCode})</span>
                    <span className="tabular-nums">−{format(couponDiscount)}</span>
                  </div>
                )}

                <div className="flex justify-between text-sm text-stone">
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>

                {totalSavings > 0 && (
                  <div className="rounded-lg bg-moss/8 border border-moss/20 px-3 py-2 flex items-center gap-2">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" className="text-moss shrink-0">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                    </svg>
                    <p className="text-xs text-moss font-medium">You're saving {format(totalSavings)} on this order!</p>
                  </div>
                )}

                <div className="flex justify-between border-t border-stone-light pt-3 text-base font-semibold text-ink">
                  <span>Total</span>
                  <span className="tabular-nums">{format(total)}</span>
                </div>
              </div>

              <div className="px-6 pb-5 space-y-3">
                <CouponSection />

                <Link
                  to="/checkout"
                  className="block w-full rounded-full bg-ink py-3.5 text-center text-[13px] font-semibold uppercase tracking-widest text-cream transition-all duration-200 hover:bg-clay hover:shadow-lg active:scale-[0.98]"
                >
                  Proceed to Checkout
                </Link>
              </div>

              <div className="border-t border-stone-light px-6 py-4">
                <TrustStrip />
              </div>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
